/**
 * Exchanges and returns.
 *
 * Bethlehem gift-shopping means this screen gets used constantly, so it is built
 * around the way it actually happens: someone walks in with a receipt (or a gift
 * receipt) and wants a different size.
 *
 * The rule that matters most: a returned item is valued at what was PAID for it,
 * discounts included, not at today's shelf price. A shirt bought at half price
 * and swapped for a full-price one costs the customer the real difference.
 *
 * A gift receipt never shows a price on paper, so the person who was given the
 * present does not find out what it cost.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  checkExchange,
  newId,
  settleExchange,
  type CatalogItem,
  type Permission,
  type ReturnableLine,
} from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useSync } from '../../core/sync.js';
import { useToast } from '../../core/state/toast.js';
import { api, ApiError } from '../../core/api.js';
import { cachedSaleByReceipt, db, findByBarcode, markCachedReturned, searchItems } from '../../core/db.js';
import { listenForScanner } from '../../core/scanner.js';
import { beepOk, beepUnknown } from '../../core/sound.js';
import { deviceId, takeReceiptNumber } from '../../core/device.js';
import { printReceipt, type ReceiptData } from '../../core/print.js';
import { Button, Empty, Field, Modal, Pill, Swatch } from '../../core/ui/ui.js';
import { CameraButton, useApproval } from '../../core/ui/bits.js';
import { PaymentDialog, type TakenPayment } from '../till/Payment.js';

interface FoundLine extends ReturnableLine {
  nameEn: string;
  nameAr: string;
  size: string;
  colorEn?: string;
  colorAr?: string;
}

interface Found {
  saleId: string;
  receiptNo: string;
  createdAt: string;
  lines: FoundLine[];
  daysSincePurchase: number;
  withinWindow: boolean;
  fromCache: boolean;
}

export function Exchange() {
  const t = useT();
  const { lang } = useI18n();
  const { settings, money } = useSettings();
  const { user } = useAuth();
  const { submitSale, state } = useSync();
  const toast = useToast();
  const approval = useApproval();

  const [code, setCode] = useState('');
  const [found, setFound] = useState<Found | null>(null);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [giftMode, setGiftMode] = useState(false);

  /** Which of the original lines are coming back, and how many of each. */
  const [returning, setReturning] = useState<Record<string, { qty: number; damaged: boolean }>>({});
  const [newItems, setNewItems] = useState<{ id: string; item: CatalogItem; qty: number }[]>([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [refundAsCash, setRefundAsCash] = useState(false);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState<{ receipt: ReceiptData; gift: boolean; creditCode?: string } | null>(null);
  const [searchResults, setSearchResults] = useState<Record<string, unknown>[]>([]);
  /** Taking something back with no receipt at all. */
  const [noReceipt, setNoReceipt] = useState(false);
  /** Approvals gathered before the payment dialog, carried through to the save. */
  const pendingApprovals = useRef<{ reason: string; managerId: string }[]>([]);

  // --- finding the receipt --------------------------------------------------

  const lookup = useCallback(
    async (raw: string) => {
      const c = raw.trim();
      if (!c) return;
      setSearching(true);
      setNotFound(false);
      try {
        const res = await api<{
          sale: Record<string, unknown>;
          lines: Record<string, unknown>[];
          daysSincePurchase: number;
          withinWindow: boolean;
        }>(`/exchange/lookup?code=${encodeURIComponent(c)}`);
        beepOk();
        setFound({
          saleId: String(res.sale.id),
          receiptNo: String(res.sale.receipt_no),
          createdAt: String(res.sale.created_at),
          daysSincePurchase: res.daysSincePurchase,
          withinWindow: res.withinWindow,
          fromCache: false,
          lines: res.lines
            .filter((l) => Number(l.qty) > 0)
            .map((l) => ({
              saleLineId: String(l.id),
              variantId: String(l.variant_id),
              qty: Number(l.qty),
              alreadyReturnedQty: Number(l.returned_qty),
              paidUnit: Number(l.net_unit),
              finalSale: Number(l.final_sale) === 1,
              nameEn: String(l.name_en),
              nameAr: String(l.name_ar),
              size: String(l.size),
              colorEn: (l.color_en as string) ?? '',
              colorAr: (l.color_ar as string) ?? '',
            })),
        });
        setReturning({});
        setNewItems([]);
      } catch (e) {
        // Offline, or a receipt the server does not know: try the device cache,
        // which holds every sale inside the exchange window.
        const cached = await cachedSaleByReceipt(c);
        if (cached) {
          beepOk();
          const days = Math.floor((Date.now() - new Date(cached.sale.created_at).getTime()) / 86_400_000);
          setFound({
            saleId: cached.sale.id,
            receiptNo: cached.sale.receipt_no,
            createdAt: cached.sale.created_at,
            daysSincePurchase: days,
            withinWindow: days <= settings.exchangeWindowDays,
            fromCache: true,
            lines: cached.lines
              .filter((l) => l.qty > 0)
              .map((l) => ({
                saleLineId: l.id,
                variantId: l.variant_id,
                qty: l.qty,
                alreadyReturnedQty: l.returned_qty,
                paidUnit: l.net_unit,
                finalSale: l.final_sale === 1,
                nameEn: l.name_en,
                nameAr: l.name_ar,
                size: l.size,
                colorEn: l.color_en,
                colorAr: l.color_ar,
              })),
          });
          setReturning({});
          setNewItems([]);
        } else {
          beepUnknown();
          setNotFound(true);
          if (e instanceof ApiError && e.isOffline) toast.warn(t('error.network'));
        }
      } finally {
        setSearching(false);
      }
    },
    [settings.exchangeWindowDays, t, toast],
  );

  // A scanned receipt barcode jumps straight to the sale.
  useEffect(
    () =>
      listenForScanner({
        onScan: (scanned) => {
          if (noReceipt) void addNoReceiptItem(scanned);
          else if (found) void addNewByBarcode(scanned);
          else void lookup(scanned);
        },
        enabled: () => !paying && !done,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [found, paying, done, lookup, noReceipt],
  );

  /**
   * One box for everything. A receipt number (scanned or typed) opens that
   * receipt; anything else - a name, a phone - searches, and a single match
   * opens straight away.
   */
  const find = async (raw: string) => {
    const q = raw.trim();
    if (!q) return;
    if (/^[A-Za-z]{1,3}-?\d{3,}$/.test(q)) {
      await lookup(q);
      return;
    }
    setSearching(true);
    setNotFound(false);
    try {
      const rows = await api<Record<string, unknown>[]>(`/exchange/search?q=${encodeURIComponent(q)}`);
      setSearchResults(rows);
      if (rows.length === 0) setNotFound(true);
      if (rows.length === 1) await lookup(String(rows[0]!.receipt_no));
    } catch {
      toast.warn(t('net.needOnline'));
    } finally {
      setSearching(false);
    }
  };

  /**
   * No receipt at all. Each item is valued at the LOWEST price it actually sold
   * for recently, so nobody can buy in a sale and return at full price. It needs
   * a manager, and the screen says so before anything is scanned.
   */
  const startNoReceipt = () => {
    setNoReceipt(true);
    setFound({
      saleId: '',
      receiptNo: '',
      createdAt: new Date().toISOString(),
      daysSincePurchase: 0,
      withinWindow: true,
      fromCache: false,
      lines: [],
    });
    setReturning({});
    setNewItems([]);
  };

  /** Scanning while in no-receipt mode adds the item to what is coming back. */
  const addNoReceiptItem = async (scanned: string) => {
    const item = await findByBarcode(scanned);
    if (!item) {
      beepUnknown();
      toast.warn(t('checkout.unknownBarcode', { code: scanned }));
      return;
    }
    let value = item.price;
    let basedOnSales = false;
    try {
      const res = await api<{ value: number; basedOnSales: boolean }>(
        `/exchange/no-receipt-value/${item.variantId}`,
      );
      value = res.value;
      basedOnSales = res.basedOnSales;
    } catch {
      // Offline: fall back to today's price and say so.
      toast.warn(t('exchange.noReceiptOffline'));
    }
    beepOk();
    const lineId = newId();
    setFound((f) =>
      f
        ? {
            ...f,
            lines: [
              ...f.lines,
              {
                saleLineId: lineId,
                variantId: item.variantId,
                qty: 1,
                alreadyReturnedQty: 0,
                paidUnit: value,
                finalSale: item.finalSale,
                nameEn: item.nameEn,
                nameAr: item.nameAr,
                size: item.size,
                colorEn: item.colorEn,
                colorAr: item.colorAr,
              },
            ],
          }
        : f,
    );
    setReturning((r) => ({ ...r, [lineId]: { qty: 1, damaged: false } }));
    if (!basedOnSales) toast.warn(t('exchange.noReceiptNoHistory'));
  };

  // --- the new items --------------------------------------------------------

  const addNewByBarcode = async (scanned: string) => {
    const item = await findByBarcode(scanned);
    if (item) {
      beepOk();
      setNewItems((l) => [...l, { id: newId(), item, qty: 1 }]);
    } else {
      beepUnknown();
      toast.warn(t('checkout.unknownBarcode', { code: scanned }));
    }
  };

  useEffect(() => {
    let alive = true;
    const timer = setTimeout(async () => {
      if (query.trim().length < 2) {
        setResults([]);
        return;
      }
      const rows = await searchItems(query, 20);
      if (alive) setResults(rows);
    }, 140);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query]);

  // --- the settlement -------------------------------------------------------

  const selected = useMemo(
    () =>
      Object.entries(returning)
        .filter(([, v]) => v.qty > 0)
        .map(([id, v]) => ({
          line: found!.lines.find((l) => l.saleLineId === id)!,
          qty: v.qty,
          damaged: v.damaged,
        }))
        .filter((x) => x.line),
    [returning, found],
  );

  const settlement = useMemo(
    () =>
      settleExchange(
        selected.map(({ line, qty }) => ({ line, qty })),
        newItems.map((n) => ({
          id: n.id,
          variantId: n.item.variantId,
          qty: n.qty,
          unitPrice: n.item.price,
          unitCost: n.item.avgCost,
        })),
        settings.vatRate,
      ),
    [selected, newItems, settings.vatRate],
  );

  const rules = useMemo(() => {
    if (!found) return null;
    return checkExchange(
      {
        soldAt: new Date(found.createdAt),
        lines: selected.map(({ line, qty }) => ({ line, qty })),
        hasReceipt: !noReceipt,
        refundAsCash,
      },
      {
        windowDays: settings.exchangeWindowDays,
        allowCashRefund: settings.allowCashRefund,
        noReceiptLookbackDays: settings.noReceiptLookbackDays,
        enforceFinalSale: settings.enforceFinalSale,
      },
    );
  }, [found, selected, refundAsCash, settings, noReceipt]);

  const explainFor = (reason: string): string => {
    switch (reason) {
      case 'outside_window':
        return t('exchange.outsideWindow', { days: found?.daysSincePurchase ?? 0, window: settings.exchangeWindowDays });
      case 'final_sale':
        return t('exchange.finalSaleBlocked');
      case 'cash_refund':
        return t('approval.reasonCashRefund');
      case 'no_receipt':
        return t('exchange.noReceiptWarn', { days: settings.noReceiptLookbackDays });
      default:
        return t('approval.title');
    }
  };

  const proceed = async () => {
    if (!found || selected.length === 0) {
      toast.warn(t('exchange.nothingSelected'));
      return;
    }
    if (!rules?.allowed) {
      toast.err(rules?.blocked[0] ?? t('error.generic'));
      return;
    }

    // Each rule that was tripped maps to the permission that lifts it. Anyone
    // who already holds it is not asked; anyone who does not needs a sign-in
    // from somebody who does - whatever their job title happens to be.
    const NEEDS: Record<string, Permission> = {
      outside_window: 'exchange.outside_window',
      no_receipt: 'exchange.no_receipt',
      final_sale: 'exchange.final_sale',
      cash_refund: 'exchange.refund_cash',
    };
    const approvals: { reason: string; managerId: string }[] = [];
    for (const reason of rules.needsApproval) {
      const permission = NEEDS[reason];
      if (!permission) continue;
      const approved = await approval.request([permission], reason, explainFor(reason));
      if (!approved) return;
      approvals.push({ reason, managerId: approved.managerId });
    }

    if (settlement.customerPays > 0) {
      setPaying(true);
      pendingApprovals.current = approvals;
      return;
    }
    await finish([], approvals, 0, 0);
  };

  const finish = async (
    payments: TakenPayment[],
    approvals: { reason: string; managerId: string }[],
    change: number,
    rounding: number,
  ) => {
    setPaying(false);
    if (!found) return;

    const { receiptNo, counter } = takeReceiptNumber();
    const saleId = newId();
    const isPureReturn = newItems.length === 0;

    const payload = {
      id: saleId,
      receiptNo,
      kind: isPureReturn ? ('return' as const) : ('exchange' as const),
      deviceId: deviceId(),
      deviceCounter: counter,
      userId: user!.id,
      originalSaleId: found.saleId || null,
      createdAt: new Date().toISOString(),
      vatRate: settings.vatRate,
      approvals,
      lines: [
        ...selected.map(({ line, qty, damaged }) => ({
          id: newId(),
          variantId: line.variantId,
          qty,
          isReturn: true,
          unitPrice: line.paidUnit,
          originalSaleLineId: noReceipt ? null : line.saleLineId,
          returnToStock: !damaged,
          nameEn: line.nameEn,
          nameAr: line.nameAr,
          size: line.size,
          colorEn: line.colorEn,
          colorAr: line.colorAr,
        })),
        ...newItems.map((n) => ({
          id: n.id,
          variantId: n.item.variantId,
          qty: n.qty,
          nameEn: n.item.nameEn,
          nameAr: n.item.nameAr,
          size: n.item.size,
          colorEn: n.item.colorEn,
          colorAr: n.item.colorAr,
          unitPrice: n.item.price,
        })),
      ],
      payments: payments.map((p) => ({
        id: p.id,
        method: p.method,
        currency: p.currency,
        amount: p.amount,
        rateMicros: p.rateMicros,
        decimals: p.decimals,
        reference: p.reference ?? null,
      })),
      // Money owed back becomes store credit unless the owner allows cash AND a
      // manager approved it on this exchange.
      refund:
        settlement.customerReceives > 0
          ? { mode: refundAsCash ? ('cash' as const) : ('credit' as const) }
          : null,
    };

    const res = await submitSale(saleId, payload);

    // The cached copy of the original receipt has to know these pieces went
    // back, or the same items could be returned twice on an offline till.
    await Promise.all(
      selected.map(({ line, qty }) => markCachedReturned(line.saleLineId, qty).catch(() => undefined)),
    );

    // Keep the cached stock honest for the next scan.
    await Promise.all([
      ...selected.map(({ line, qty, damaged }) =>
        damaged
          ? Promise.resolve()
          : db.items
              .get(line.variantId)
              .then((i) => i && db.items.update(line.variantId, { stock: i.stock + qty }))
              .catch(() => undefined),
      ),
      ...newItems.map((n) =>
        db.items.update(n.item.variantId, { stock: Math.max(0, n.item.stock - n.qty) }).catch(() => undefined),
      ),
    ]);

    const receipt: ReceiptData = {
      receiptNo,
      createdAt: payload.createdAt,
      cashierName: user!.name,
      kind: payload.kind,
      lines: [
        ...selected.map(({ line, qty }) => ({
          nameEn: `${line.nameEn} (${t('exchange.chooseReturning')})`,
          nameAr: `${line.nameAr} (${t('exchange.chooseReturning')})`,
          size: line.size,
          colorEn: line.colorEn,
          colorAr: line.colorAr,
          qty: -qty,
          unitPrice: line.paidUnit,
          net: -line.paidUnit * qty,
        })),
        ...newItems.map((n) => ({
          nameEn: n.item.nameEn,
          nameAr: n.item.nameAr,
          size: n.item.size,
          colorEn: n.item.colorEn,
          colorAr: n.item.colorAr,
          qty: n.qty,
          unitPrice: n.item.price,
          net: n.item.price * n.qty,
        })),
      ],
      gross: settlement.newItemsValue,
      discountTotal: 0,
      total: settlement.balance,
      rounding,
      payable: settlement.balance + rounding,
      vatAmount: Math.round(settlement.balance - settlement.balance / (1 + settings.vatRate / 100)),
      netAmount: Math.round(settlement.balance / (1 + settings.vatRate / 100)),
      vatRate: settings.vatRate,
      changeIls: change,
      payments: payments.map((p) => ({
        // The method, not a label: the receipt names it in the SHOP's language.
        method: p.method,
        methodLabel: p.methodLabel,
        currency: p.currency,
        amount: p.amount,
        amountIls: Math.round((p.amount * p.rateMicros * 100) / (10 ** p.decimals * 1_000_000)),
        rate: p.rateMicros / 1_000_000,
      })),
      // The server allocates the voucher code and hands it straight back, so it
      // can be printed on the exchange receipt. Offline, the credit is created
      // when the sale uploads and the voucher is printed from Store credit then.
      creditIssued:
        settlement.customerReceives > 0 && !refundAsCash && res.result?.creditIssued
          ? res.result.creditIssued
          : null,
    };

    toast.ok(t('exchange.completed'));
    setDone({ receipt, gift: giftMode });
    setFound(null);
    setReturning({});
    setNewItems([]);
    setCode('');
    setNoReceipt(false);
  };

  // --- render ---------------------------------------------------------------

  if (!found) {
    return (
      <div className="col" style={{ maxWidth: 720, marginInline: 'auto' }}>
        <div className="card">
          <h2>{t('exchange.scanReceipt')}</h2>
          <p className="small muted">{t('exchange.scanHint')}</p>
          <div className="row" style={{ marginBlockStart: 'var(--s-4)' }}>
            <input
              data-scan-target="true"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void find(code)}
              placeholder={t('exchange.findPlaceholder')} aria-label={t('exchange.findPlaceholder')}
              className="big"
              autoFocus
            />
            <CameraButton onScan={(c) => void lookup(c)} />
            <Button variant="primary" size="lg" busy={searching} onClick={() => void find(code)}>
              {t('common.search')}
            </Button>
          </div>
          {notFound ? <p style={{ color: 'var(--danger)' }}>{t('error.notFound')}</p> : null}
          {searchResults.length > 1 ? (
            <div className="table-wrap" style={{ marginBlockStart: 'var(--s-3)' }}>
              <table className="cards">
                <tbody>
                  {searchResults.map((r) => (
                    <tr key={String(r.id)} onClick={() => void lookup(String(r.receipt_no))} style={{ cursor: 'pointer' }}>
                      <td className="strong">{String(r.receipt_no)}</td>
                      <td>{new Date(String(r.created_at)).toLocaleDateString('en-GB')}</td>
                      <td>{(r.customer_name as string) ?? ''}</td>
                      <td className="num tnum">{money(Number(r.total))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="row" style={{ marginBlockStart: 'var(--s-4)' }}>
            <Button variant="ghost" onClick={startNoReceipt}>
              {t('exchange.noReceipt')}
            </Button>
            <span className="xsmall muted">
              {t('exchange.noReceiptWarn', { days: settings.noReceiptLookbackDays })}
            </span>
          </div>
        </div>

        {state === 'offline' ? (
          <p className="small muted center">{t('net.offlineNote')}</p>
        ) : null}

        {done ? <ExchangeDone done={done} onClose={() => setDone(null)} /> : null}
      </div>
    );
  }

  const nm = (l: { nameEn: string; nameAr: string }) =>
    lang === 'ar' ? l.nameAr || l.nameEn : l.nameEn || l.nameAr;

  return (
    <div className="grid c2" style={{ alignItems: 'start' }}>
      {/* what is coming back */}
      <div className="card">
        <div className="card-head">
          <h2>{t('exchange.chooseReturning')}</h2>
          <div className="spacer" />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFound(null);
              setNoReceipt(false);
            }}
          >
            {t('common.back')}
          </Button>
        </div>

        {noReceipt ? (
          <div className="note-warn">
            {t('exchange.noReceiptWarn', { days: settings.noReceiptLookbackDays })}
          </div>
        ) : null}

        {noReceipt ? (
          <div className="row" style={{ marginBlockEnd: 'var(--s-4)' }}>
            <input
              data-scan-target="true"
              placeholder={t('exchange.scanTheItem')} aria-label={t('exchange.scanTheItem')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  void addNoReceiptItem((e.target as HTMLInputElement).value);
                  (e.target as HTMLInputElement).value = '';
                }
              }}
              autoFocus
            />
            <CameraButton onScan={(c) => void addNoReceiptItem(c)} />
          </div>
        ) : null}

        <div className="row wrap" style={{ marginBlockEnd: 'var(--s-4)' }}>
          {noReceipt ? <Pill kind="warn">{t('exchange.noReceipt')}</Pill> : <Pill kind="accent">{found.receiptNo}</Pill>}
          {!noReceipt ? (
            <>
              <Pill>{t('exchange.soldOn')} {new Date(found.createdAt).toLocaleDateString('en-GB')}</Pill>
              <Pill kind={found.withinWindow ? 'ok' : 'warn'}>
                {t('exchange.daysAgo', { n: found.daysSincePurchase })}
              </Pill>
            </>
          ) : null}
          {found.fromCache ? <Pill kind="info">{t('net.offline')}</Pill> : null}
        </div>

        {found.lines.map((l) => {
          const left = l.qty - l.alreadyReturnedQty;
          const sel = returning[l.saleLineId];
          return (
            <div key={l.saleLineId} className="ret-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="strong">{nm(l)}</div>
                <div className="meta xsmall muted">
                  {l.size} · <Swatch hex={undefined} />
                  {lang === 'ar' ? l.colorAr : l.colorEn} · {t('exchange.paidFor')}{' '}
                  <strong className="tnum">{money(l.paidUnit)}</strong>
                </div>
                <div className="row wrap" style={{ gap: 'var(--s-2)', marginBlockStart: 4 }}>
                  {l.alreadyReturnedQty > 0 ? <Pill kind="warn">{t('exchange.alreadyExchanged')}</Pill> : null}
                  {l.finalSale ? <Pill kind="danger">{t('checkout.finalSale')}</Pill> : null}
                  {sel?.qty ? (
                    <label className="checkline xsmall">
                      <input
                        type="checkbox"
                        checked={sel.damaged}
                        onChange={(e) =>
                          setReturning((r) => ({ ...r, [l.saleLineId]: { ...sel, damaged: e.target.checked } }))
                        }
                      />
                      {t('exchange.returnedDamaged')}
                    </label>
                  ) : null}
                </div>
              </div>

              <div className="qty">
                <button
                  onClick={() =>
                    setReturning((r) => ({
                      ...r,
                      [l.saleLineId]: { qty: Math.max(0, (sel?.qty ?? 0) - 1), damaged: sel?.damaged ?? false },
                    }))
                  }
                  disabled={!sel?.qty}
                >
                  −
                </button>
                <span className="n">{sel?.qty ?? 0}</span>
                <button
                  onClick={() =>
                    setReturning((r) => ({
                      ...r,
                      [l.saleLineId]: { qty: Math.min(left, (sel?.qty ?? 0) + 1), damaged: sel?.damaged ?? false },
                    }))
                  }
                  disabled={(sel?.qty ?? 0) >= left}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}

        <label className="checkline" style={{ marginBlockStart: 'var(--s-4)' }}>
          <input type="checkbox" checked={giftMode} onChange={(e) => setGiftMode(e.target.checked)} />
          {t('exchange.giftPrivacy')}
        </label>
      </div>

      {/* what they are taking */}
      <div className="card">
        <h2>{t('exchange.chooseNew')}</h2>
        <div className="row" style={{ marginBlock: 'var(--s-3)', position: 'relative' }}>
          <input
            data-scan-target="true"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('common.searchPlaceholder')}
          />
          <CameraButton onScan={(c) => void addNewByBarcode(c)} />
        </div>

        {results.length > 0 ? (
          <div className="col" style={{ gap: 'var(--s-1)', marginBlockEnd: 'var(--s-4)' }}>
            {results.slice(0, 8).map((r) => (
              <button
                key={r.variantId}
                className="pick-row"
                onClick={() => {
                  setNewItems((l) => [...l, { id: newId(), item: r, qty: 1 }]);
                  setQuery('');
                  setResults([]);
                }}
              >
                <span style={{ flex: 1, textAlign: 'start' }}>
                  {nm(r)} · {r.size} · {lang === 'ar' ? r.colorAr : r.colorEn}
                </span>
                <span className="tnum strong">{money(r.price)}</span>
              </button>
            ))}
          </div>
        ) : null}

        {newItems.length === 0 ? (
          <p className="small muted">{t('exchange.returnOnly')}</p>
        ) : (
          newItems.map((n) => (
            <div key={n.id} className="ret-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="strong">{nm(n.item)}</div>
                <div className="xsmall muted">
                  {n.item.size} · {lang === 'ar' ? n.item.colorAr : n.item.colorEn}
                </div>
              </div>
              <div className="tnum strong">{money(n.item.price * n.qty)}</div>
              <Button size="sm" variant="ghost" aria-label={t('checkout.removeLine')} onClick={() => setNewItems((l) => l.filter((x) => x.id !== n.id))}>
                ✕
              </Button>
            </div>
          ))
        )}

        {/* the balance */}
        <div className="totals-panel" style={{ marginBlockStart: 'var(--s-4)' }}>
          <div className="r">
            <span>{t('exchange.chooseReturning')}</span>
            <span className="tnum">−{money(settlement.returnedValue)}</span>
          </div>
          <div className="r">
            <span>{t('exchange.chooseNew')}</span>
            <span className="tnum">{money(settlement.newItemsValue)}</span>
          </div>
          <div className="r grand">
            {settlement.balance === 0 ? (
              <>
                <span>{t('exchange.evenSwap')}</span>
                <span className="v tnum">{money(0)}</span>
              </>
            ) : settlement.balance > 0 ? (
              <>
                <span>{t('exchange.customerPays')}</span>
                <span className="v tnum">{money(settlement.customerPays)}</span>
              </>
            ) : (
              <>
                <span>{t('exchange.customerGets')}</span>
                <span className="v tnum">{money(settlement.customerReceives)}</span>
              </>
            )}
          </div>
        </div>

        {settlement.customerReceives > 0 ? (
          <div className="col" style={{ gap: 'var(--s-2)', marginBlockStart: 'var(--s-3)' }}>
            {settings.allowCashRefund ? (
              <div className="btn-row">
                <Button variant={!refundAsCash ? 'primary' : 'default'} onClick={() => setRefundAsCash(false)}>
                  {t('exchange.asCredit')}
                </Button>
                <Button variant={refundAsCash ? 'primary' : 'default'} onClick={() => setRefundAsCash(true)}>
                  {t('exchange.asCash')}
                </Button>
              </div>
            ) : (
              <p className="small muted">{t('exchange.cashRefundOff')}</p>
            )}
          </div>
        ) : null}

        {rules && rules.needsApproval.length > 0 ? (
          <div className="note-warn">
            {rules.needsApproval.map((r) => (
              <div key={r}>{explainFor(r)}</div>
            ))}
          </div>
        ) : null}
        {rules && !rules.allowed ? <div className="note-danger">{rules.blocked.join(' ')}</div> : null}

        <Button
          variant="primary"
          size="lg"
          block
          style={{ marginBlockStart: 'var(--s-4)' }}
          disabled={selected.length === 0 || !rules?.allowed}
          onClick={() => void proceed()}
        >
          {t('exchange.complete')}
        </Button>
      </div>

      {paying ? (
        <PaymentDialog
          total={settlement.customerPays}
          allowCredit
          onDone={(p, change, rounding) => void finish(p, pendingApprovals.current, change, rounding)}
          onCancel={() => setPaying(false)}
        />
      ) : null}

      {done ? <ExchangeDone done={done} onClose={() => setDone(null)} /> : null}
      {approval.element}
    </div>
  );
}

function ExchangeDone({
  done,
  onClose,
}: {
  done: { receipt: ReceiptData; gift: boolean };
  onClose: () => void;
}) {
  const t = useT();
  const { settings, money } = useSettings();
  return (
    <Modal
      title={t('exchange.completed')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.close')}</Button>
          <div className="spacer" />
          <Button variant="primary" onClick={() => void printReceipt(done.receipt, settings, done.gift)}>
            {t('exchange.printReceipt')}
          </Button>
        </>
      }
    >
      <div className="col center" style={{ alignItems: 'center', gap: 'var(--s-3)' }}>
        <div style={{ fontSize: '2.5rem' }} aria-hidden>✓</div>
        <div className="small muted">{t('receipt.no')} {done.receipt.receiptNo}</div>
        {done.receipt.creditIssued ? (
          <Pill kind="accent">
            {t('exchange.asCredit')}: {money(done.receipt.creditIssued.amount)}
          </Pill>
        ) : null}
        {done.gift ? <Pill kind="info">{t('receipt.giftNote')}</Pill> : null}
      </div>
    </Modal>
  );
}
